import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface Instalador {
  id: string;
  nome: string;
  cpf_cnpj?: string;
  contato?: string;
  email?: string;
  ativo: boolean;
  created_at: string;
  updated_at: string;
}

export function useInstaladores() {
  return useQuery({
    queryKey: ['instaladores'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('instaladores')
        .select('*')
        .eq('ativo', true)
        .order('nome');

      if (error) {
        console.error('Erro ao buscar instaladores:', error);
        throw error;
      }

      return data as Instalador[];
    },
  });
}

export function useCreateInstalador() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (instalador: Omit<Instalador, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('instaladores')
        .insert(instalador)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instaladores'] });
      toast.success('Instalador cadastrado com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao cadastrar instalador');
    },
  });
}

export function useEditarInstalador() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (instalador: { id: string; nome: string; cpf_cnpj?: string; contato?: string; email?: string; ativo: boolean }) => {
      const { data, error } = await supabase
        .from('instaladores')
        .update({
          nome: instalador.nome,
          cpf_cnpj: instalador.cpf_cnpj,
          contato: instalador.contato,
          email: instalador.email,
          ativo: instalador.ativo,
          updated_at: new Date().toISOString()
        })
        .eq('id', instalador.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instaladores'] });
      toast.success('Instalador editado com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao editar instalador');
    },
  });
}

export function useExcluirInstalador() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('instaladores')
        .update({ ativo: false, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instaladores'] });
      toast.success('Instalador excluído com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao excluir instalador');
    },
  });
}